import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, ArrowRight, Loader2, Music4 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ASSET_BASE, ASSETS } from "@/lib/api";
import { resilientGet, ssrBodyFor } from "@/lib/resilientGet";
import { PrerenderFallback } from "@/components/PrerenderFallback";
import { useLocale, restoreBaselineHead } from "@/lib/LocaleContext";
import { localisedTo } from "@/lib/i18nRoutes";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";

const ARABIC_MONTHS = [
  "يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو",
  "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر",
];

function fmtDate(iso, lang) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (lang === "ar") {
      return `${d.getDate()} ${ARABIC_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
    }
    return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return "";
  }
}

// Rough words-per-minute estimate — 220 wpm for EN, 180 for AR.
function estimateReadingMinutes(markdown, lang) {
  const raw = String(markdown || "").trim();
  if (!raw) return 1;
  const cleaned = raw
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`[^`]*`/g, " ")
    .replace(/!\[[^\]]*]\([^)]*\)/g, " ")
    .replace(/\[([^\]]*)]\([^)]*\)/g, "$1")
    .replace(/[#>*_~\-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const words = cleaned ? cleaned.split(" ").length : 0;
  const wpm = lang === "ar" ? 180 : 220;
  return Math.max(1, Math.round(words / wpm));
}

function injectMeta(name, value, attr = "name") {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value || "");
}

function injectJsonLd(id, obj) {
  const existing = document.getElementById(id);
  if (existing) existing.remove();
  const s = document.createElement("script");
  s.type = "application/ld+json";
  s.id = id;
  s.text = JSON.stringify(obj);
  document.head.appendChild(s);
}

// Managed hreflang alternates. Removes ALL prior link[rel="alternate"][hreflang]
// tags (including static ones baked into index.html which point at "/") before
// re-injecting the current page's alternates. Idempotent across navigations.
const HREFLANG_MARKER = "data-djspy-hreflang";
function setHreflangAlternates(alternates) {
  document
    .querySelectorAll('link[rel="alternate"][hreflang]')
    .forEach((el) => el.remove());
  if (!alternates) return;
  Object.entries(alternates).forEach(([lang, href]) => {
    const el = document.createElement("link");
    el.setAttribute("rel", "alternate");
    el.setAttribute("hreflang", lang);
    el.setAttribute("href", href.startsWith("http") ? href : `https://djspyofficial.com${href}`);
    el.setAttribute(HREFLANG_MARKER, "1");
    document.head.appendChild(el);
  });
}

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { lang } = useLocale();
  const isArabicRoute = lang === "ar" || pathname.startsWith("/ar/");
  const [state, setState] = useState({ status: "loading", post: null, error: null });

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading", post: null, error: null });
    const langParam = isArabicRoute ? "ar" : "en";
    resilientGet(`/blog/resolve/${encodeURIComponent(slug)}?lang=${langParam}`)
      .then((r) => {
        if (cancelled) return;
        const post = r.data;
        // Slug rotation — the old slug 301s to the new canonical.
        if (post.redirected && post.canonical_slug && post.canonical_slug !== slug) {
          const target = isArabicRoute
            ? `/ar/blog/${post.canonical_slug}`
            : `/blog/${post.canonical_slug}`;
          navigate(target, { replace: true });
          return;
        }
        setState({ status: "ok", post, error: null });
      })
      .catch((err) => {
        if (cancelled) return;
        const code = err?.response?.status;
        setState({
          status: code === 404 ? "not-found" : "error",
          post: null,
          error: err?.response?.data?.detail || "Could not load this post.",
        });
      });
    return () => { cancelled = true; };
  }, [slug, navigate, isArabicRoute]);

  // ────────────────────────────────────────────────────────────────────
  // CLIENT-SIDE SEO MITIGATION ONLY — confirmed-404 handling.
  // Mirror of the TrackPage mitigation. Applies only after the backend
  // positively returns 404 for this blog slug. Never while loading,
  // never on a transient error, never on a valid post. Does NOT
  // convert the Production edge HTTP status (static-host returns 200).
  // ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (state.status !== "not-found" && state.status !== "error") return;
    // Generic error with prerendered content: the first-response head is already correct.
    if (state.status === "error" && ssrBodyFor(pathname)) return;
    const setMeta = (name, content, attr = "name") => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("robots", "noindex,nofollow");
    document.querySelectorAll('link[rel="canonical"]').forEach((n) => n.remove());
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((n) => n.remove());
    const social = [...document.querySelectorAll('meta[property^="og:"], meta[name^="twitter:"]')];
    social.forEach((n) => n.remove());
    document.querySelectorAll('script[type="application/ld+json"][data-ld="blog"]').forEach((n) => n.remove());
    document.title = state.status === "not-found" ? `${t("notFound.title")} | DJ SPY` : "Something went wrong | DJ SPY";
    setMeta("description", "The story you were looking for was not found.");
    return () => { restoreBaselineHead(); social.forEach((n) => document.head.appendChild(n)); };
  }, [state.status, pathname, t]);

  const canonicalUrl = useMemo(() => {
    if (!state.post) return null;
    const canonicalSlug = state.post.canonical_slug || state.post.slug || slug;
    const prefix = isArabicRoute ? "/ar/blog" : "/blog";
    return `https://djspyofficial.com${prefix}/${canonicalSlug}`;
  }, [state.post, slug, isArabicRoute]);

  useEffect(() => {
    if (state.status !== "ok" || !state.post) return;
    const p = state.post;
    const title = p.seo_title || `${p.title} — DJ SPY Official`;
    const desc = p.meta_description || `The story behind ${p.title}.`;
    document.title = title;
    injectMeta("description", desc);
    // Slice C — Arabic pages are indexable once translation is published.
    injectMeta("robots", "index,follow");
    // Open Graph / Twitter for social previews
    injectMeta("og:title", title, "property");
    injectMeta("og:description", desc, "property");
    injectMeta("og:type", "article", "property");
    injectMeta("og:locale", isArabicRoute ? "ar_AR" : "en_US", "property");
    if (canonicalUrl) injectMeta("og:url", canonicalUrl, "property");
    if (p.cover_url) injectMeta("og:image", `${ASSET_BASE}${p.cover_url}`, "property");
    injectMeta("twitter:card", "summary_large_image");

    // Canonical <link>
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    if (canonicalUrl) link.setAttribute("href", canonicalUrl);

    // hreflang alternates — from API response, or self-only when no counterpart.
    setHreflangAlternates(p.alternates || null);

    // BlogPosting JSON-LD for rich results
    injectJsonLd("blog-jsonld", {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "inLanguage": isArabicRoute ? "ar" : "en",
      "headline": p.title,
      "description": desc,
      "image": p.cover_url ? [`${ASSET_BASE}${p.cover_url}`] : undefined,
      "datePublished": p.published_at,
      "dateModified": p.updated_at || p.published_at,
      "author": { "@type": "Person", "name": "DJ SPY" },
      "publisher": {
        "@type": "Organization",
        "name": "DJ SPY Official",
        "logo": {
          "@type": "ImageObject",
          "url": "https://djspyofficial.com/logo.png",
        },
      },
      "mainEntityOfPage": canonicalUrl,
    });

    // FAQPage JSON-LD — only when the post actually ships FAQ entries.
    // Rich results eligible in Google Search.
    if (Array.isArray(p.faq) && p.faq.length > 0) {
      injectJsonLd("blog-faq-jsonld", {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": p.faq.map((f) => ({
          "@type": "Question",
          "name": f.question,
          "acceptedAnswer": { "@type": "Answer", "text": f.answer },
        })),
      });
    }

    return () => {
      const el = document.getElementById("blog-jsonld");
      if (el) el.remove();
      const faqEl = document.getElementById("blog-faq-jsonld");
      if (faqEl) faqEl.remove();
      // NOTE: intentionally do NOT clear hreflang on unmount — the next
      // route (LocaleContext / other pages) is responsible for injecting
      // its own hreflang set, and we clear-then-replace on mount above.
    };
  }, [state, canonicalUrl, isArabicRoute]);

  if (state.status === "loading") {
    return (
      <>
        <Nav />
        <main className="min-h-screen bg-black text-white pt-32 pb-24 px-6 md:px-10">
          <div className="max-w-3xl mx-auto flex items-center gap-3 text-white/50 font-mono text-xs tracking-widest">
            <Loader2 size={14} className="animate-spin" /> {isArabicRoute ? "جارٍ التحميل…" : "LOADING…"}
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (state.status === "not-found") {
    return (
      <>
        <Nav />
        <main
          data-testid="blog-post-404"
          data-lang={isArabicRoute ? "ar" : "en"}
          dir={isArabicRoute ? "rtl" : "ltr"}
          className="min-h-screen bg-black text-white pt-32 pb-24 px-6 md:px-10"
        >
          <div className="max-w-2xl mx-auto text-center">
            <p className="font-mono text-xs text-red-brand tracking-[0.3em]">// 404</p>
            <h1 className={`${isArabicRoute ? "font-display" : "font-anton"} text-5xl md:text-6xl mt-3`}>
              {isArabicRoute ? "لم يتم العثور على المقال" : "POST NOT FOUND"}
            </h1>
            <p className="text-white/60 mt-4">
              {isArabicRoute
                ? "هذه القصة غير موجودة أو لم تُترجم بعد إلى العربية."
                : "This story doesn't exist or has been retired."}
            </p>
            <div className="mt-8 flex items-center gap-3 justify-center">
              <Link
                to={localisedTo("/blog", lang)}
                className="btn-ghost text-sm"
                data-testid="blog-404-back-to-list"
              >
                <ArrowLeft size={12} /> {isArabicRoute ? "المدونة" : "All posts"}
              </Link>
              <Link
                to={localisedTo("/#music", lang)}
                className="btn-primary text-sm"
                data-testid="blog-404-browse-music"
              >
                {isArabicRoute ? "تصفح المتجر" : "Browse tracks"} <ArrowRight size={12} />
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (state.status === "error") {
    const ssrEl = ssrBodyFor(pathname);
    if (ssrEl) return <PrerenderFallback el={ssrEl} testid="blog-post-prerender-fallback" />;
    return (
      <>
        <Nav />
        <main className="min-h-screen bg-black text-white pt-32 pb-24 px-6 md:px-10" data-testid="blog-post-error">
          <div className="max-w-2xl mx-auto">
            <p className="font-mono text-xs text-red-brand tracking-[0.3em]">// ERROR</p>
            <h1 className="font-anton text-4xl mt-3">SOMETHING BROKE</h1>
            <p className="text-white/60 mt-3">{state.error}</p>
            <Link to={localisedTo("/blog", lang)} className="btn-ghost text-sm mt-6 inline-flex"><ArrowLeft size={12} /> Back to blog</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const p = state.post;
  const cover = p.cover_url ? `${ASSET_BASE}${p.cover_url}` : ASSETS.logoCover;
  const headingFont = isArabicRoute ? "font-display" : "font-anton";
  const arSubtitle = isArabicRoute ? (p.subtitle || "") : "";
  const arLead = isArabicRoute ? (p.excerpt || "") : "";

  return (
    <>
      <Nav />
      <main
        data-testid="page-blog-post"
        data-lang={isArabicRoute ? "ar" : "en"}
        dir={isArabicRoute ? "rtl" : "ltr"}
        className="min-h-screen bg-black text-white"
      >
        {/* Cinematic hero */}
        <div className="relative h-[54vh] md:h-[64vh] w-full overflow-hidden">
          <img
            src={cover}
            alt={`${p.title} cover art`}
            className="absolute inset-0 w-full h-full object-cover opacity-70"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/70 to-black" />
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-4xl mx-auto px-6 md:px-10 pb-10 md:pb-16 w-full">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Link
                  to={localisedTo("/blog", lang)}
                  className="font-mono text-[10px] tracking-[0.3em] text-red-brand hover:text-white transition-colors"
                >
                  {isArabicRoute ? "→ المدونة" : "← THE JOURNAL"}
                </Link>
                {isArabicRoute ? (
                  arSubtitle && (
                    <p
                      data-testid="blog-post-subtitle"
                      className="font-display text-sm md:text-base text-white/80 mt-4 font-semibold"
                    >
                      {arSubtitle}
                    </p>
                  )
                ) : (
                  (p.genre || p.artist) && (
                    <p
                      data-testid="blog-post-subtitle"
                      className="font-mono text-[10px] tracking-widest text-white/60 mt-4"
                    >
                      {p.genre && <span>{p.genre.toUpperCase()}</span>}
                      {p.genre && p.artist && <span className="text-white/30"> • </span>}
                      {p.artist && <span>{p.artist}</span>}
                    </p>
                  )
                )}
                <h1
                  data-testid="blog-post-title"
                  className={`${headingFont} text-4xl md:text-6xl lg:text-7xl leading-[1.05] mt-3 max-w-4xl`}
                >
                  {p.title}
                  {!isArabicRoute && p.artist && (
                    <span className="text-white/70"> by {p.artist}</span>
                  )}
                  {!isArabicRoute && (
                    <span className="text-red-brand">
                      {" "}— DJ SPY{p.genre ? ` ${p.genre}` : ""} Remix
                    </span>
                  )}
                </h1>
                {isArabicRoute && arLead && (
                  <p
                    data-testid="blog-post-lead"
                    className="mt-4 text-white/70 text-base md:text-lg leading-relaxed max-w-2xl"
                  >
                    {arLead}
                  </p>
                )}
                <p
                  data-testid="blog-post-meta"
                  className="font-mono text-[10px] tracking-[0.2em] text-white/45 mt-4"
                >
                  {fmtDate(p.published_at, lang)}
                  <span className="text-white/25"> · </span>
                  <span data-testid="blog-post-reading-time">
                    {estimateReadingMinutes(p.content_markdown, lang)}{" "}
                    {isArabicRoute ? "دقيقة قراءة" : "min read"}
                  </span>
                  <span className="text-white/25"> · </span>
                  DJ SPY
                </p>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Article */}
        <article
          dir={isArabicRoute ? "rtl" : "ltr"}
          className="max-w-3xl mx-auto px-6 md:px-10 py-14 md:py-20"
        >
          <div
            data-testid="blog-post-content"
            className={isArabicRoute ? "prose-blog prose-ar" : "prose-blog"}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {p.content_markdown || (isArabicRoute ? "*(لا يوجد محتوى بعد.)*" : "*(This post has no body content yet.)*")}
            </ReactMarkdown>
          </div>

          {/* Listen CTA — routes back to the track on the main store */}
          <div
            data-testid="blog-listen-card"
            className="mt-14 hairline rounded-2xl p-6 md:p-8 bg-white/[0.03] flex flex-col md:flex-row md:items-center gap-5"
          >
            <img
              src={cover}
              alt=""
              className="w-20 h-20 md:w-24 md:h-24 rounded-lg object-cover border border-white/10 shrink-0"
            />
            <div className="flex-1">
              <p className="font-mono text-[10px] tracking-widest text-red-brand">
                {isArabicRoute ? "// استمع" : "// LISTEN"}
              </p>
              <p
                data-testid="blog-listen-copy"
                className="mt-2 text-white text-lg md:text-xl leading-snug"
              >
                {isArabicRoute ? (
                  <>
                    جاهز لسماع الريمكس كاملًا؟ استمتع بـ{" "}
                    <span className="font-display text-red-brand">{p.title}</span>{" "}
                    من DJ SPY بجودة عالية.
                  </>
                ) : (
                  <>
                    Ready to hear the full remix? Experience{" "}
                    <span className="font-display text-red-brand">{p.title}</span>
                    {" "}by DJ SPY{p.artist ? <> and <span className="text-white">{p.artist}</span></> : null} in
                    full quality.
                  </>
                )}
              </p>
            </div>
            <Link
              to={localisedTo("/#music", lang)}
              data-testid="blog-listen-cta"
              className="btn-primary text-sm justify-center shrink-0"
            >
              <Music4 size={14} /> {isArabicRoute ? "استمع للتراك" : "Hear the Track"}
            </Link>
          </div>

          <div className="mt-10 flex items-center justify-between text-xs font-mono tracking-widest">
            <Link to={localisedTo("/blog", lang)} className="text-white/60 hover:text-white transition-colors">
              <ArrowLeft size={12} className="inline mr-1" />{" "}
              {isArabicRoute ? "كل المقالات" : "ALL POSTS"}
            </Link>
            <Link to={localisedTo("/#music", lang)} className="text-red-brand hover:text-white transition-colors">
              {isArabicRoute ? "المتجر" : "THE CATALOGUE"} <ArrowRight size={12} className="inline ml-1" />
            </Link>
          </div>
        </article>

        {/* FAQ — accessible <details>/<summary>, rendered as real HTML for
            crawlability. Only shown when the post ships FAQ entries. */}
        {!isArabicRoute && Array.isArray(p.faq) && p.faq.length > 0 && (
          <section
            data-testid="blog-faq"
            aria-label="Frequently asked questions"
            className="border-t border-white/10 bg-black"
          >
            <div className="max-w-3xl mx-auto px-6 md:px-10 py-14 md:py-20">
              <p className="font-mono text-[10px] text-red-brand tracking-[0.3em]">// FAQ</p>
              <h2 className="font-anton text-3xl md:text-5xl mt-3">FANS ALSO ASK</h2>
              <p className="text-white/60 text-sm mt-3 max-w-xl">
                Quick answers to the things people search for about this track.
              </p>

              <ul
                data-testid="blog-faq-list"
                className="mt-8 space-y-3 list-none pl-0"
              >
                {p.faq.map((f, idx) => (
                  <li key={idx} className="m-0">
                    <details
                      data-testid={`blog-faq-item-${idx}`}
                      className="group hairline rounded-xl bg-white/[0.02] overflow-hidden open:border-red-brand/40 transition-colors"
                    >
                      <summary
                        className="cursor-pointer list-none px-5 py-4 flex items-center justify-between gap-4 text-white font-display text-base md:text-lg leading-tight"
                        data-testid={`blog-faq-question-${idx}`}
                      >
                        <span>{f.question}</span>
                        <span
                          aria-hidden="true"
                          className="shrink-0 font-mono text-red-brand text-lg leading-none transition-transform duration-200 group-open:rotate-45"
                        >
                          +
                        </span>
                      </summary>
                      <div
                        className="px-5 pb-5 pt-1 text-white/70 text-sm leading-relaxed"
                        data-testid={`blog-faq-answer-${idx}`}
                      >
                        {f.answer}
                      </div>
                    </details>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* You might also like — related posts, rendered as real anchor tags */}
        {Array.isArray(p.related) && p.related.length > 0 && (
          <section
            data-testid="blog-related"
            aria-label={isArabicRoute ? "قد يعجبك أيضًا" : "You might also like"}
            className="border-t border-white/10 bg-black"
          >
            <div className="max-w-6xl mx-auto px-6 md:px-10 py-14 md:py-20">
              <p className="font-mono text-[10px] text-red-brand tracking-[0.3em]">
                {isArabicRoute ? "// استمر بالاستماع" : "// KEEP LISTENING"}
              </p>
              <h2 className={`${headingFont} text-3xl md:text-5xl mt-3`}>
                {isArabicRoute ? "قد يعجبك أيضًا" : "YOU MIGHT ALSO LIKE"}
              </h2>
              {!isArabicRoute && (
                <p className="text-white/60 text-sm mt-3 max-w-xl">
                  More field notes on remixes cut from the same cloth — same artist,
                  same genre, or freshly released in the DJ SPY music store.
                </p>
              )}

              <ul
                data-testid="blog-related-list"
                className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 list-none pl-0"
              >
                {p.related.map((r) => {
                  const rCover = r.cover_url ? `${ASSET_BASE}${r.cover_url}` : ASSETS.logoCover;
                  const rHref = isArabicRoute ? `/ar/blog/${r.slug}` : `/blog/${r.slug}`;
                  return (
                    <li key={r.id || r.slug} className="m-0">
                      <Link
                        to={rHref}
                        data-testid={`blog-related-${r.slug}`}
                        rel="related"
                        className="group block hairline rounded-xl overflow-hidden bg-white/[0.02] hover:border-red-brand/40 transition-colors"
                      >
                        <div className="aspect-[16/10] relative overflow-hidden bg-black/40">
                          <img
                            src={rCover}
                            alt={`${r.title} cover art`}
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          />
                          {r.genre && (
                            <span className="absolute top-2 left-2 font-mono text-[9px] tracking-widest bg-black/70 backdrop-blur px-2 py-1 rounded text-white/80" dir="ltr">
                              {r.genre.toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div className="p-4">
                          <h3 className={`${isArabicRoute ? "font-display" : "font-display"} text-base md:text-lg text-white group-hover:text-red-brand transition-colors leading-tight`}>
                            {r.title}
                          </h3>
                          {r.artist && (
                            <p className="font-mono text-[10px] tracking-widest text-white/50 mt-1" dir="ltr">
                              {r.artist.toUpperCase()}
                            </p>
                          )}
                          <span className="mt-3 inline-flex items-center gap-1 text-[11px] font-mono tracking-widest text-red-brand group-hover:gap-2 transition-all">
                            {isArabicRoute ? "اقرأ" : "READ"} <ArrowRight size={11} />
                          </span>
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>

              {/* Fallback for crawlers / users without JS — plain-text anchor list */}
              <noscript>
                <ul>
                  {p.related.map((r) => (
                    <li key={`ns-${r.id || r.slug}`}>
                      <a href={isArabicRoute ? `/ar/blog/${r.slug}` : `/blog/${r.slug}`}>{r.title}</a>
                    </li>
                  ))}
                </ul>
              </noscript>
            </div>
          </section>
        )}

        {/* Stage 4 — internal linking: similar tracks in the same genre as this
            post's source track. Excludes the current track and any deleted rows. */}
        {Array.isArray(p.similar_tracks) && p.similar_tracks.length > 0 && (
          <section
            className="max-w-[1100px] mx-auto mt-16 border-t border-white/10 pt-14"
            data-testid="blog-similar-tracks"
          >
            <p className="font-mono text-xs text-red-brand tracking-[0.3em]">
              {isArabicRoute ? "// تراكات مشابهة" : "// SIMILAR TRACKS"}
            </p>
            <h2 className={`${headingFont} text-3xl md:text-5xl text-white mt-2`}>
              {isArabicRoute ? `المزيد من ${p.genre || ""} من DJ SPY` : `More ${p.genre} from DJ SPY`}
            </h2>
            <ul
              className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4"
              data-testid="blog-similar-tracks-list"
            >
              {p.similar_tracks.map((tr) => (
                <li key={tr.id}>
                  <Link
                    to={localisedTo(tr.blog_slug ? `/blog/${tr.blog_slug}` : "/#music", lang)}
                    data-testid={`blog-similar-track-${tr.id}`}
                    className="group block hairline rounded-xl overflow-hidden bg-white/[0.02] hover:border-red-brand/60 transition-colors"
                    aria-label={`${tr.title} — ${tr.genre}`}
                  >
                    <div className="aspect-square bg-black relative">
                      {tr.cover_url ? (
                        <img
                          src={tr.cover_url.startsWith("http") ? tr.cover_url : `${window.location.origin}${tr.cover_url}`}
                          alt=""
                          loading="lazy"
                          className="w-full h-full object-cover"
                        />
                      ) : null}
                      {tr.is_free && (
                        <span className="absolute top-2 right-2 bg-red-brand text-white font-mono text-[9px] tracking-widest px-1.5 py-0.5 rounded">
                          {isArabicRoute ? "مجاني" : "FREE"}
                        </span>
                      )}
                    </div>
                    <div className="p-3">
                      <p className="font-display text-sm text-white leading-tight line-clamp-2 group-hover:text-red-brand transition-colors">
                        {tr.title}
                      </p>
                      <p className="font-mono text-[10px] tracking-widest text-white/40 mt-1" dir="ltr">
                        {tr.genre?.toUpperCase()}
                      </p>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
            {/* Crawlable anchor fallback */}
            <noscript>
              <ul>
                {p.similar_tracks.map((tr) => (
                  <li key={`ns-${tr.id}`}>
                    {tr.blog_slug ? (
                      <a href={isArabicRoute ? `/ar/blog/${tr.blog_slug}` : `/blog/${tr.blog_slug}`}>{tr.title}</a>
                    ) : (
                      <a href={`/genre/${(tr.genre || "").toLowerCase().replace(/&/g, " and ").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}>{tr.title}</a>
                    )}
                  </li>
                ))}
              </ul>
            </noscript>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}
